import { useCallback, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Loader2, ImagePlus } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { EMAIL_IMAGES_URL } from "../../constants/api";

/**
 * Rich-text editor for email bodies.
 *
 * The one thing this adds over a plain ReactQuill is the image handler. Quill's
 * default embeds a chosen image as a base64 data: URI, which is the worst
 * possible form for email — Gmail and Outlook strip data: URIs outright, and the
 * base64 payload pushes the message past the ~102KB point where Gmail clips the
 * body behind a "View entire message" link, hiding the unsubscribe footer and
 * breaking tracking below the cut. Images are uploaded instead, and the public
 * URL is inserted.
 *
 * Layout is not previewed accurately here and is not meant to be: this shows
 * browser rendering, while the email is re-rendered server-side into a table
 * shell before sending. Use the Preview button for what actually ships.
 */
export default function EmailBodyEditor({ value, onChange, height = 300, placeholder }) {
    const quillRef = useRef(null);
    const { create } = useApiMethods();
    const [uploading, setUploading] = useState(false);

    const insertImage = useCallback(async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            ToastMsg("error", "Image is larger than 5MB. Resize it and try again.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await create(EMAIL_IMAGES_URL, fd, true);
            if (res?.status === "success" && res.data?.url) {
                const editor = quillRef.current?.getEditor?.();
                if (editor) {
                    const range = editor.getSelection(true) || { index: editor.getLength() };
                    editor.insertEmbed(range.index, "image", res.data.url, "user");
                    editor.setSelection(range.index + 1);
                }
            } else {
                ToastMsg("error", res?.message || "Could not upload the image");
            }
        } catch {
            ToastMsg("error", "Could not upload the image");
        }
        setUploading(false);
    }, [create]);

    const imageHandler = useCallback(() => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/png,image/jpeg,image/gif,image/webp";
        input.onchange = () => insertImage(input.files?.[0]);
        input.click();
    }, [insertImage]);

    // Memoised: rebuilding `modules` on every render tears down and recreates
    // the Quill instance, which loses the cursor and the undo history.
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                ["link", "image"],
                ["clean"],
            ],
            handlers: { image: imageHandler },
        },
        clipboard: { matchVisual: false },
    }), [imageHandler]);

    return (
        <div className="relative">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={value}
                    onChange={onChange}
                    modules={modules}
                    placeholder={placeholder}
                    style={{ height }}
                />
            </div>

            {uploading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-lg bg-white/70 text-sm text-gray-600 dark:bg-gray-900/70 dark:text-gray-300">
                    <Loader2 className="h-4 w-4 animate-spin" /> Uploading image…
                </div>
            )}

            <p className="mt-11 flex items-start gap-1.5 text-[11px] text-gray-400">
                <ImagePlus className="mt-px h-3.5 w-3.5 flex-shrink-0" />
                Images are uploaded and linked, not embedded — embedded images are stripped by
                Gmail and Outlook. Keep them under 600px wide; they scale down on phones
                automatically.
            </p>
        </div>
    );
}
